import { useState, useEffect } from "react";
import { Plus, Trash2, ToggleLeft, ToggleRight, Copy, CheckCircle, AlertCircle, Cpu, Key } from "lucide-react";

interface ProviderKey {
  id: string;
  provider: "openai" | "anthropic" | "google" | "ericoder";
  label: string;
  is_active: boolean;
  created_at: string;
}

const PROVIDER_META = {
  openai: { name: "OpenAI", logo: "🟢", color: "#10a37f", models: "gpt-4o, gpt-4o-mini, gpt-4-turbo...", placeholder: "sk-proj-..." },
  anthropic: { name: "Anthropic", logo: "🟠", color: "#d97706", models: "claude-3-5-sonnet, claude-3-opus...", placeholder: "sk-ant-..." },
  google: { name: "Google Gemini", logo: "🔵", color: "#4285f4", models: "gemini-1.5-pro, gemini-2.0-flash...", placeholder: "AIza..." },
  ericoder: { name: "Ericoder IDE", logo: "🚀", color: "#38bdf8", models: "Erix MEC Pairing", placeholder: "sk-eri-..." },
};

export function ProvidersPage() {
  const [keys, setKeys] = useState<ProviderKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ provider: "openai", api_key: "", label: "" });
  const [saving, setSaving] = useState(false);
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);
  const token = localStorage.getItem("eric_token");
  const GATEWAY_URL = "https://mec.wowerix.com/v1";

  const fetchKeys = async () => {
    try {
      const res = await fetch(`${GATEWAY_URL}/admin/provider-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) setKeys(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchKeys(); }, []);

  const handleAdd = async () => {
    if (!form.api_key.trim()) return alert("Vui long nhap API Key!");
    setSaving(true);
    try {
      const res = await fetch(`${GATEWAY_URL}/admin/provider-keys`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.status === 401) {
        localStorage.removeItem('eric_token');
        window.location.href = '/login';
        return;
      }
      const data = await res.json();
      if (res.ok && data.success) {
        setShowForm(false);
        setForm({ provider: "openai", api_key: "", label: "" });
        fetchKeys();
      } else { alert("Loi: " + (data.error || 'Server error') + (data.details ? ` (${data.details})` : '')); }
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string, label: string) => {
    if (!confirm(`Xoa key "${label}"? Hanh dong nay khong the hoan tac.`)) return;
    await fetch(`${GATEWAY_URL}/admin/provider-keys/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchKeys();
  };

  const handleToggle = async (id: string) => {
    await fetch(`${GATEWAY_URL}/admin/provider-keys/${id}/toggle`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchKeys();
  };

  const copyEndpoint = () => {
    navigator.clipboard.writeText(GATEWAY_URL);
    setCopiedEndpoint(true);
    setTimeout(() => setCopiedEndpoint(false), 2000);
  };

  const selectedMeta = PROVIDER_META[form.provider as keyof typeof PROVIDER_META];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px", maxWidth: "900px", margin: "0 auto" }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
          <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: "var(--gradient-main)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Cpu size={20} color="#fff" />
          </div>
          <div>
            <h2 style={{ fontSize: "24px", fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>AI Providers</h2>
            <p style={{ color: "var(--text-muted)", fontSize: "13px", margin: 0 }}>Cau hinh API Key cua cac LLM Provider de kich hoat AI Gateway (Mo hinh 2)</p>
          </div>
        </div>
      </div>

      <div className="glass-panel" style={{ padding: "20px", border: "1px solid rgba(139, 92, 246, 0.3)", background: "linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(0,0,0,0.2))" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 8px #22c55e" }} />
          <span style={{ color: "var(--text-primary)", fontSize: "13px", fontWeight: 600 }}>GATEWAY ENDPOINT (OpenAI-Compatible)</span>
        </div>
        <p style={{ color: "var(--text-secondary)", fontSize: "13px", marginBottom: "16px" }}>
          Dev chi can thay <code style={{ background: "rgba(255,255,255,0.05)", padding: "2px 6px", borderRadius: "4px", color: "var(--accent-primary)" }}>baseURL</code> trong Agent tro vao endpoint nay. Erix MEC se tu dong inject memory va forward den LLM that.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
          <div style={{ flex: 1, background: "rgba(0,0,0,0.4)", border: "1px solid rgba(139,92,246,0.3)", borderRadius: "8px", padding: "10px 16px", fontFamily: "monospace", fontSize: "14px", color: "var(--accent-primary)" }}>
            {GATEWAY_URL}
          </div>
          <button onClick={copyEndpoint} className="btn btn-glass" style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 16px", whiteSpace: "nowrap" }}>
            {copiedEndpoint ? <><CheckCircle size={14} color="#22c55e" /> Copied!</> : <><Copy size={14} /> Copy URL</>}
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
          {[
            { name: "Python / OpenAI SDK", code: `from openai import OpenAI\nclient = OpenAI(\n  base_url="${GATEWAY_URL}",\n  api_key="<your_token>"\n)` },
            { name: "Cursor / Continue.dev", code: `openai:\n  baseURL: "${GATEWAY_URL}"\n  apiKey: "<your_token>"` },
            { name: "LangChain", code: `ChatOpenAI(\n  openai_api_base=\n    "${GATEWAY_URL}",\n  openai_api_key="<token>"\n)` },
          ].map((ex) => (
            <div key={ex.name} style={{ background: "rgba(0,0,0,0.3)", borderRadius: "8px", padding: "12px", border: "1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ color: "var(--text-muted)", fontSize: "11px", fontWeight: 600, marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{ex.name}</div>
              <pre style={{ color: "var(--text-secondary)", fontSize: "11px", margin: 0, whiteSpace: "pre-wrap", fontFamily: "monospace", lineHeight: 1.6 }}>{ex.code}</pre>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>Configured Providers ({keys.length})</h3>
          <button onClick={() => setShowForm(true)} className="btn btn-primary" style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", fontSize: "13px" }}>
            <Plus size={14} /> Add Provider Key
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>Loading...</div>
        ) : keys.length === 0 ? (
          <div className="glass-panel" style={{ padding: "40px", textAlign: "center", border: "1px dashed rgba(255,255,255,0.1)" }}>
            <AlertCircle size={32} color="var(--text-muted)" style={{ marginBottom: "12px" }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>Chua co provider nao duoc cau hinh.</p>
            <p style={{ color: "var(--text-muted)", fontSize: "13px", margin: "4px 0 0" }}>Nhan "Add Provider Key" de bat dau kich hoat AI Gateway.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {keys.map((k) => {
              const meta = PROVIDER_META[k.provider] || { name: k.provider, logo: "???", color: "#666", models: "" };
              return (
                <div key={k.id} className="glass-panel" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: "16px", border: `1px solid ${k.is_active ? "rgba(34, 197, 94, 0.2)" : "rgba(255,255,255,0.05)"}`, opacity: k.is_active ? 1 : 0.6 }}>
                  <span style={{ fontSize: "24px" }}>{meta.logo}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>{k.label}</span>
                      <span style={{ background: k.is_active ? "rgba(34, 197, 94, 0.15)" : "rgba(255,255,255,0.05)", color: k.is_active ? "#22c55e" : "var(--text-muted)", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: 600 }}>
                        {k.is_active ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </div>
                    <div style={{ color: "var(--text-muted)", fontSize: "12px", marginTop: "2px" }}>{meta.name} · {meta.models}</div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => handleToggle(k.id)} className="btn btn-glass" style={{ padding: "6px 12px", fontSize: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                      {k.is_active ? <ToggleRight size={14} color="#22c55e" /> : <ToggleLeft size={14} />}
                      {k.is_active ? "Disable" : "Enable"}
                    </button>
                    <button onClick={() => handleDelete(k.id, k.label)} className="btn btn-glass" style={{ padding: "6px 10px", color: "#ef4444", borderColor: "rgba(239,68,68,0.3)" }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(8px)" }}>
          <div className="glass-panel" style={{ width: "480px", padding: "32px", border: "1px solid rgba(255,255,255,0.1)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
              <Key size={20} color="var(--accent-primary)" />
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "var(--text-primary)" }}>Add Provider API Key</h3>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", color: "var(--text-muted)", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Provider</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
                {(Object.keys(PROVIDER_META) as Array<keyof typeof PROVIDER_META>).map((p) => (
                  <button key={p} onClick={() => setForm((f) => ({ ...f, provider: p }))}
                    style={{ padding: "10px", borderRadius: "8px", border: `2px solid ${form.provider === p ? PROVIDER_META[p].color : "rgba(255,255,255,0.1)"}`, background: form.provider === p ? `${PROVIDER_META[p].color}20` : "rgba(0,0,0,0.2)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", transition: "all 0.2s" }}>
                    <span style={{ fontSize: "20px" }}>{PROVIDER_META[p].logo}</span>
                    <span style={{ fontSize: "11px", color: form.provider === p ? "var(--text-primary)" : "var(--text-muted)", fontWeight: 600 }}>{PROVIDER_META[p].name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", color: "var(--text-muted)", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Label</label>
              <input type="text" placeholder={`e.g. ${selectedMeta.name} Production`}
                value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.3)", color: "var(--text-primary)", fontSize: "14px", outline: "none", boxSizing: "border-box" }} />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{ display: "block", color: "var(--text-muted)", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>API Key</label>
              <input type="password" placeholder={selectedMeta.placeholder}
                value={form.api_key} onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
                style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.3)", color: "var(--text-primary)", fontSize: "14px", fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} />
              <p style={{ color: "var(--text-muted)", fontSize: "11px", marginTop: "6px" }}>
                Khoa AES-256-GCM truoc khi luu. Chung toi khong bao gio hien thi key that sau khi luu.
              </p>
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => { setShowForm(false); setForm({ provider: "openai", api_key: "", label: "" }); }}
                className="btn btn-glass" style={{ flex: 1 }}>Huy</button>
              <button onClick={handleAdd} disabled={saving} className="btn btn-primary" style={{ flex: 1 }}>
                {saving ? "Dang luu..." : "Them Key"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
